import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Camera, Upload, User as UserIcon, Phone, MapPin, Building2, IdCard, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";

function mask(s, last = 4) {
  if (!s) return "—";
  if (s.length <= last) return s;
  return "X".repeat(s.length - last) + s.slice(-last);
}

function Section({ title, Icon, children }) {
  return (
    <section className="bg-white rounded-xl border border-red-100 p-5">
      <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 brand-text" /> {title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {children}
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}

export default function PortalProfile() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState({ aadhaar: false, pan: false, account: false });
  const [uploading, setUploading] = useState(false);
  const photoInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/my-portal/me");
      setMe(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo must be 2 MB or less / Photo 2 MB se kam honi chahiye");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/employees/${me.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading...</div>;
  if (!me) return <div className="text-sm text-slate-500">No profile data</div>;

  return (
    <div className="space-y-6" data-testid="portal-profile">
      {/* Header card with photo */}
      <div className="bg-gradient-to-r from-red-50 to-white rounded-xl border border-red-100 p-5 flex items-center gap-5 flex-wrap">
        <div className="h-24 w-24 rounded-lg bg-white border border-red-100 overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="portal-profile-photo">
          {me.profile_photo_url ? (
            <img src={`/api/files/${me.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
          ) : (
            <Camera className="h-8 w-8 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Employee</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>{me.name}</h1>
          <div className="text-sm text-slate-600 mt-1">
            {me.punch_id} · {me.department?.name || "—"} · {me.unit?.name || "—"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{me.email}</div>
        </div>
        <div>
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPhoto}
            data-testid="portal-photo-input"
          />
          <Button
            onClick={() => photoInput.current?.click()}
            disabled={uploading}
            className="brand-bg brand-bg-hover text-white"
            data-testid="portal-photo-upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading..." : me.profile_photo_url ? "Change Photo" : "Upload Photo"}
          </Button>
        </div>
      </div>

      <Section title="Personal" Icon={UserIcon}>
        <Row label="Father / Husband" value={me.father_husband_name} />
        <Row label="Date of Birth" value={me.dob ? formatDate(me.dob) : "—"} />
        <Row label="Mobile" value={me.mobile} />
        <Row label="Emergency Contact" value={me.emergency_contact_person
          ? `${me.emergency_contact_person} (${me.emergency_contact_relation || "—"}) · ${me.emergency_contact_number || ""}`
          : "—"} />
        <Row label="Permanent Address" value={me.permanent_address} />
        <Row label="Communication Address" value={me.communication_address} />
      </Section>

      <Section title="Identity (Aadhaar / PAN)" Icon={IdCard}>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Aadhaar</div>
          <div className="text-slate-900 font-medium mt-0.5 flex items-center gap-2">
            <span data-testid="me-aadhaar">
              {reveal.aadhaar ? (me._aadhaar_full || "—") : mask(me._aadhaar_full)}
            </span>
            {me._aadhaar_full && (
              <button onClick={() => setReveal((r) => ({ ...r, aadhaar: !r.aadhaar }))} className="text-xs brand-text hover:underline">
                {reveal.aadhaar ? "Hide" : "Show"}
              </button>
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">PAN</div>
          <div className="text-slate-900 font-medium mt-0.5 flex items-center gap-2">
            <span data-testid="me-pan">
              {reveal.pan ? (me._pan_full || "—") : mask(me._pan_full)}
            </span>
            {me._pan_full && (
              <button onClick={() => setReveal((r) => ({ ...r, pan: !r.pan }))} className="text-xs brand-text hover:underline">
                {reveal.pan ? "Hide" : "Show"}
              </button>
            )}
          </div>
        </div>
        <Row label="UAN (PF)" value={me.uan} />
        <Row label="ESI Code" value={me.esi_code} />
      </Section>

      <Section title="Employment" Icon={Building2}>
        <Row label="Date of Joining" value={me.doj ? formatDate(me.doj) : "—"} />
        <Row label="Department" value={me.department?.name} />
        <Row label="Unit" value={me.unit?.name} />
        <Row label="Status" value={me.status} />
        <Row label="Shift" value={me.shift_start ? `${me.shift_start} (${me.shift_hours}h)` : "—"} />
        <Row label="Category" value={me.category} />
        <Row label="HOD / Manager" value={me.reports_to_hod_user
          ? `${me.reports_to_hod_user.name} (${me.reports_to_hod_user.email})`
          : "—"} />
        <Row label="Senior Management" value={me.reports_to_senior_mgmt_user
          ? `${me.reports_to_senior_mgmt_user.name} (${me.reports_to_senior_mgmt_user.email})`
          : "—"} />
      </Section>

      <Section title="Bank" Icon={Banknote}>
        <Row label="Bank Name" value={me.bank_name} />
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Account No</div>
          <div className="text-slate-900 font-medium mt-0.5 flex items-center gap-2">
            <span data-testid="me-account">
              {reveal.account ? (me._account_full || "—") : mask(me._account_full)}
            </span>
            {me._account_full && (
              <button onClick={() => setReveal((r) => ({ ...r, account: !r.account }))} className="text-xs brand-text hover:underline">
                {reveal.account ? "Hide" : "Show"}
              </button>
            )}
          </div>
        </div>
        <Row label="IFSC" value={me.ifsc} />
        <Row label="Account Type" value={me.account_type} />
      </Section>

      <p className="text-xs text-slate-500">
        To update any of this information, please reach out to your HR / HOD.
      </p>
    </div>
  );
}
